import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FiArrowRight,
  FiClock,
  FiCornerDownLeft,
  FiInbox,
  FiPlus,
  FiSearch,
  FiUser,
  FiUserCheck,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Kbd, cn } from '../ui';
import { ALL_NAV_ITEMS } from './navigation';
import { useEntityCommands } from './useEntityCommands';

const RECENTS_KEY = 'betterfly:cmdk:recents';
const RECENTS_MAX = 6;

function readRecents() {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
function writeRecents(list) {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, RECENTS_MAX)));
  } catch {}
}

/**
 * Fuzzy-ish match: case-insensitive substring, scored by earliest match
 * position (lower = better). Returns `null` if no match.
 */
function score(query, text) {
  if (!query) return 0;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  return idx < 0 ? null : idx;
}

/**
 * CommandPalette — Cmd+K spotlight.
 *
 * Builds its command list from:
 *   1. Navigation (always)
 *   2. Quick actions (New client, etc.)
 *   3. Recents (persisted, up to 6)
 *   4. Entities — clients / partners / requests (lazy-fetched on open)
 */
export function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const [recents, setRecents] = useState(readRecents);

  const { clients, partners, requests, ensureLoaded } = useEntityCommands();

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelected(0);
      setRecents(readRecents());
      ensureLoaded();
      // Focus after portal paints.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open, ensureLoaded]);

  const registerRecent = useCallback((cmd) => {
    const lean = {
      id: cmd.id,
      label: cmd.label,
      sectionLabel: cmd.sectionLabel || cmd.group,
      path: cmd.path,
      kind: cmd.kind,
    };
    if (!lean.path) return;
    setRecents((prev) => {
      const next = [lean, ...prev.filter((r) => r.id !== lean.id)];
      writeRecents(next);
      return next.slice(0, RECENTS_MAX);
    });
  }, []);

  const run = useCallback(
    (cmd) => {
      if (!cmd) return;
      registerRecent(cmd);
      if (cmd.onSelect) cmd.onSelect({ navigate });
      else if (cmd.path) navigate(cmd.path);
      onClose?.();
    },
    [navigate, onClose, registerRecent],
  );

  // Build command groups from sources.
  const groups = useMemo(() => {
    const navGroup = {
      heading: 'Navigation',
      items: ALL_NAV_ITEMS.map((n) => ({
        id: `nav:${n.id}`,
        kind: 'nav',
        label: n.label,
        sectionLabel: n.sectionLabel,
        hint: n.shortcut?.toUpperCase(),
        path: n.path,
        icon: n.icon,
      })),
    };

    const actionsGroup = {
      heading: 'Actions',
      items: [
        {
          id: 'action:new-client',
          kind: 'action',
          label: 'New client',
          sectionLabel: 'Tenancy',
          icon: FiPlus,
          path: '/new-client',
        },
      ],
    };

    const clientsGroup = {
      heading: 'Clients',
      items: clients.slice(0, 50).map((c) => ({
        id: `client:${c.id}`,
        kind: 'client',
        label: c.Nom || c.Key || `Client ${c.id}`,
        sub: c.Key,
        sectionLabel: 'Client',
        icon: FiUser,
        path: `/edit-client/${c.id}`,
      })),
    };

    const partnersGroup = {
      heading: 'Partners',
      items: partners.slice(0, 50).map((p) => ({
        id: `partner:${p.id}`,
        kind: 'partner',
        label: p.company_name || `Partner ${p.id}`,
        sub: p.contact_email,
        sectionLabel: 'Partner',
        icon: FiUserCheck,
        path: `/partners/${p.id}`,
      })),
    };

    const requestsGroup = {
      heading: 'Requests',
      items: requests.slice(0, 30).map((r) => ({
        id: `request:${r.id}`,
        kind: 'request',
        label: r.company_name || `Request ${r.id}`,
        sub: r.status,
        sectionLabel: 'Request',
        icon: FiInbox,
        path: '/client-requests',
      })),
    };

    return [navGroup, actionsGroup, clientsGroup, partnersGroup, requestsGroup];
  }, [clients, partners, requests]);

  // Filter + rank.
  const filteredGroups = useMemo(() => {
    const q = query.trim();
    if (!q) {
      const recentsGroup = recents.length
        ? {
            heading: 'Recent',
            items: recents.map((r) => ({
              ...r,
              kind: r.kind || 'recent',
              icon: FiClock,
            })),
          }
        : null;
      return [recentsGroup, ...groups].filter(Boolean).map((g) => ({
        ...g,
        items: g.items.slice(0, 8),
      }));
    }

    const result = [];
    for (const g of groups) {
      const ranked = g.items
        .map((it) => {
          const haystack = `${it.label} ${it.sub || ''} ${it.sectionLabel || ''}`;
          const s = score(q, haystack);
          return s === null ? null : { item: it, s };
        })
        .filter(Boolean)
        .sort((a, b) => a.s - b.s)
        .slice(0, 8)
        .map((x) => x.item);
      if (ranked.length) result.push({ heading: g.heading, items: ranked });
    }
    return result;
  }, [groups, query, recents]);

  // Flat list for keyboard navigation.
  const flat = useMemo(
    () => filteredGroups.flatMap((g) => g.items),
    [filteredGroups],
  );

  useEffect(() => {
    if (selected >= flat.length) setSelected(Math.max(0, flat.length - 1));
  }, [flat.length, selected]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      run(flat[selected]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.();
    }
  };

  // Scroll selected item into view.
  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector(`[data-cmd-index="${selected}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selected, open]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-[70] flex items-start justify-center px-4 pt-[12vh]"
          role="dialog"
          aria-modal="true"
          aria-label="Command palette"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            onClick={onClose}
            className="absolute inset-0 bg-ink-950/50 backdrop-blur-[2px]"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: -4 }}
            transition={{ duration: 0.14, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-[620px] overflow-hidden rounded-xl border border-line bg-surface shadow-e3"
          >
            {/* Input */}
            <div className="flex items-center gap-2.5 border-b border-line px-4">
              <FiSearch size={16} className="text-fg-subtle" aria-hidden />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelected(0);
                }}
                onKeyDown={onKeyDown}
                placeholder="Search tenants, actions, navigation…"
                className="h-12 flex-1 bg-transparent text-body text-fg placeholder:text-fg-subtle focus:outline-none"
              />
              <Kbd>Esc</Kbd>
            </div>

            {/* Results */}
            <div
              ref={listRef}
              className="max-h-[52vh] overflow-y-auto overscroll-contain py-1.5"
            >
              {flat.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <p className="text-body-sm text-fg-muted">No results</p>
                  <p className="mt-1 text-caption text-fg-subtle">
                    Try a different query or navigate with <Kbd>G</Kbd>
                    <span className="mx-0.5"> </span>
                    <Kbd>C</Kbd>
                  </p>
                </div>
              ) : (
                filteredGroups.map((g) => {
                  let globalIdx = filteredGroups
                    .slice(0, filteredGroups.indexOf(g))
                    .reduce((acc, x) => acc + x.items.length, 0);
                  return (
                    <div key={g.heading} className="mb-1 last:mb-0">
                      <p className="px-3 pb-1 pt-2 text-micro text-fg-subtle">{g.heading}</p>
                      {g.items.map((item) => {
                        const idx = globalIdx++;
                        const Icon = item.icon || FiArrowRight;
                        const isSelected = idx === selected;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            data-cmd-index={idx}
                            onMouseEnter={() => setSelected(idx)}
                            onClick={() => run(item)}
                            className={cn(
                              'flex w-full items-center gap-3 px-3 py-1.5 text-left',
                              'transition-colors',
                              isSelected ? 'bg-brand-50' : 'hover:bg-ink-50',
                            )}
                          >
                            <Icon
                              size={15}
                              className={cn(
                                'shrink-0',
                                isSelected ? 'text-brand-700' : 'text-fg-muted',
                              )}
                              aria-hidden
                            />
                            <span
                              className={cn(
                                'flex-1 truncate text-body-sm',
                                isSelected ? 'text-fg font-medium' : 'text-fg',
                              )}
                            >
                              {item.label}
                              {item.sub && (
                                <span className="ml-2 text-caption text-fg-subtle">
                                  {item.sub}
                                </span>
                              )}
                            </span>
                            {item.sectionLabel && (
                              <span className="hidden text-caption text-fg-subtle sm:inline">
                                {item.sectionLabel}
                              </span>
                            )}
                            {item.hint && (
                              <span className="hidden font-mono text-[10px] text-fg-subtle sm:inline">
                                {item.hint}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer hints */}
            <div className="flex items-center justify-between border-t border-line bg-surface-muted px-4 py-2 text-caption text-fg-subtle">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1">
                  <Kbd>↑</Kbd>
                  <Kbd>↓</Kbd>
                  <span className="ml-1">navigate</span>
                </span>
                <span className="inline-flex items-center gap-1">
                  <Kbd>
                    <FiCornerDownLeft size={10} />
                  </Kbd>
                  <span className="ml-1">select</span>
                </span>
              </div>
              <span className="inline-flex items-center gap-1">
                <Kbd>G</Kbd>
                <Kbd>C</Kbd>
                <span className="ml-1">go to clients</span>
              </span>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export default CommandPalette;
